import { DocsService } from '@affine/core/modules/doc';
import { createDatabaseBlockDataSource } from '@blocksuite/affine/blocks/database';
import type { Store } from '@blocksuite/affine/store';
import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import {
  type DataViewDataType,
  type GroupBy,
  ViewManagerBase,
} from '@blocksuite/data-view';
import {
  calendarRoleAcceptsPropertyType,
  createKanbanL1GroupBy,
  isKanbanL1Groupable,
} from '@blocksuite/data-view/view-presets';
import { assertDatabaseIdentity } from '@streamlain/database-identity';
import { Service } from '@toeverything/infra';

import { waitForDatabaseStorageReady } from '../services/cross-doc-automation';

export type PlannerViewKind = 'calendar' | 'kanban' | 'gantt';

export interface PlannerSourceRef {
  docId: string;
  blockId: string;
  databaseId: string;
  viewId: string;
}

export interface PlannerViewDescriptor {
  id: string;
  name: string;
  kind: PlannerViewKind;
}

export type PlannerViewCompatibilityReason =
  | 'missing_start_date'
  | 'invalid_start_date'
  | 'missing_end_date'
  | 'invalid_end_date'
  | 'missing_group_property';

export interface PlannerViewCompatibility {
  compatible: boolean;
  reason: PlannerViewCompatibilityReason | null;
}

export type PlannerViewPortErrorCode =
  | 'aborted'
  | 'doc_unavailable'
  | 'database_unavailable'
  | 'source_view_unavailable'
  | 'readonly'
  | 'incompatible'
  | 'create_failed';

export class PlannerViewPortError extends Error {
  constructor(
    readonly code: PlannerViewPortErrorCode,
    message: string,
    readonly reason?: PlannerViewCompatibilityReason
  ) {
    super(message);
    this.name = 'PlannerViewPortError';
  }
}

export interface PlannerViewPort {
  listViews(input: {
    source: PlannerSourceRef;
    signal?: AbortSignal;
  }): Promise<{
    views: readonly PlannerViewDescriptor[];
    compatibility: {
      kanban: PlannerViewCompatibility;
      gantt: PlannerViewCompatibility;
    };
  }>;
  createView(input: {
    source: PlannerSourceRef;
    kind: 'kanban' | 'gantt';
    signal?: AbortSignal;
  }): Promise<PlannerViewDescriptor>;
}

type DatabaseContext = {
  dataSource: ReturnType<typeof createDatabaseBlockDataSource>;
  release: () => void;
};

type SharedCreate = {
  controller: AbortController;
  promise: Promise<PlannerViewDescriptor>;
  waiters: number;
};

type PlannerDatabaseDataSource = ReturnType<
  typeof createDatabaseBlockDataSource
>;

const SOURCE_VIEW_WAIT_TIMEOUT_MS = 10_000;

const supportedModes = new Set<PlannerViewKind>([
  'calendar',
  'kanban',
  'gantt',
]);

function optionalString(
  data: DataViewDataType,
  key: string
): string | undefined {
  const value = (data as unknown as Record<string, unknown>)[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function optionalGroupBy(data: DataViewDataType): GroupBy | undefined {
  const value = (data as unknown as { groupBy?: GroupBy }).groupBy;
  return value?.columnId ? value : undefined;
}

export class PlannerViewPortService extends Service implements PlannerViewPort {
  private readonly creates = new Map<string, SharedCreate>();

  constructor(private readonly docsService: DocsService) {
    super();
  }

  async listViews(input: { source: PlannerSourceRef; signal?: AbortSignal }) {
    const context = await this.openDatabase(input.source, input.signal);
    try {
      const { snapshot, sourceView } = await this.waitForSourceView(
        context.dataSource,
        input.source.viewId,
        input.signal
      );
      return {
        views: this.describeViews(snapshot),
        compatibility: {
          kanban: this.kanbanCompatibility(context.dataSource, sourceView),
          gantt: this.ganttCompatibility(context.dataSource, sourceView),
        },
      };
    } finally {
      context.release();
    }
  }

  createView(input: {
    source: PlannerSourceRef;
    kind: 'kanban' | 'gantt';
    signal?: AbortSignal;
  }): Promise<PlannerViewDescriptor> {
    this.throwIfAborted(input.signal);
    const key = JSON.stringify([
      input.source.docId,
      input.source.blockId,
      input.source.databaseId,
      input.source.viewId,
      input.kind,
    ]);
    let shared = this.creates.get(key);
    if (!shared) {
      const controller = new AbortController();
      const promise = this.createViewOnce(
        input.source,
        input.kind,
        controller.signal
      ).finally(() => this.creates.delete(key));
      shared = { controller, promise, waiters: 0 };
      this.creates.set(key, shared);
    }
    shared.waiters += 1;
    return this.waitForSharedCreate(shared, input.signal);
  }

  private async waitForSharedCreate(
    shared: SharedCreate,
    signal?: AbortSignal
  ): Promise<PlannerViewDescriptor> {
    if (!signal) return shared.promise;
    return new Promise((resolve, reject) => {
      let settled = false;
      const abort = () => {
        if (settled) return;
        settled = true;
        shared.waiters -= 1;
        if (shared.waiters === 0) shared.controller.abort();
        reject(
          new PlannerViewPortError('aborted', 'Planner operation aborted')
        );
      };
      signal.addEventListener('abort', abort, { once: true });
      shared.promise.then(
        value => {
          if (settled) return;
          settled = true;
          signal.removeEventListener('abort', abort);
          resolve(value);
        },
        error => {
          if (settled) return;
          settled = true;
          signal.removeEventListener('abort', abort);
          reject(error);
        }
      );
    });
  }

  private async createViewOnce(
    source: PlannerSourceRef,
    kind: 'kanban' | 'gantt',
    signal: AbortSignal
  ): Promise<PlannerViewDescriptor> {
    const context = await this.openDatabase(source, signal);
    try {
      const { sourceView } = await this.waitForSourceView(
        context.dataSource,
        source.viewId,
        signal
      );
      if (context.dataSource.readonly$.value) {
        throw new PlannerViewPortError('readonly', 'Database is readonly');
      }
      const compatibility =
        kind === 'gantt'
          ? this.ganttCompatibility(context.dataSource, sourceView)
          : this.kanbanCompatibility(context.dataSource, sourceView);
      if (!compatibility.compatible) {
        throw new PlannerViewPortError(
          'incompatible',
          `Source view is incompatible with ${kind}`,
          compatibility.reason ?? undefined
        );
      }

      const manager = new ViewManagerBase(context.dataSource);
      const targetViewName =
        context.dataSource.viewMetaGet(kind).model.defaultName;
      const id = manager.viewAddFrom(source.viewId, kind, {
        override: data => {
          const named = { ...data, name: targetViewName };
          return kind === 'kanban'
            ? this.withKanbanGroupBy(context.dataSource, sourceView, named)
            : named;
        },
      });
      const created = context.dataSource.viewDataGet(id);
      if (!created) {
        throw new PlannerViewPortError(
          'create_failed',
          'Created view is unavailable'
        );
      }
      return this.describeView(created);
    } catch (error) {
      if (error instanceof PlannerViewPortError) throw error;
      throw new PlannerViewPortError(
        'create_failed',
        error instanceof Error ? error.message : String(error)
      );
    } finally {
      context.release();
    }
  }

  private async openDatabase(
    source: PlannerSourceRef,
    signal?: AbortSignal
  ): Promise<DatabaseContext> {
    this.throwIfAborted(signal);
    let cached: boolean;
    try {
      cached = await this.docsService.ensureDocCached(source.docId, { signal });
    } catch (error) {
      if (signal?.aborted || this.isAbortError(error)) this.throwAborted();
      throw new PlannerViewPortError('doc_unavailable', 'Doc is unavailable');
    }
    this.throwIfAborted(signal);
    if (!cached) {
      throw new PlannerViewPortError('doc_unavailable', 'Doc is unavailable');
    }

    let opened: ReturnType<DocsService['open']>;
    try {
      opened = this.docsService.open(source.docId);
    } catch {
      throw new PlannerViewPortError('doc_unavailable', 'Doc is unavailable');
    }
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      opened.release();
    };
    try {
      this.throwIfAborted(signal);
      const store = opened.doc.blockSuiteDoc as Store;
      const block = store.getBlock(source.blockId);
      if (!block) {
        throw new PlannerViewPortError(
          'database_unavailable',
          'Database block is unavailable'
        );
      }
      const identity = assertDatabaseIdentity(block.model);
      if (identity.databaseId !== source.databaseId) {
        throw new PlannerViewPortError(
          'database_unavailable',
          'Database identity does not match the planner source'
        );
      }
      const databaseModel = block.model as DatabaseBlockModel;
      if (!(await waitForDatabaseStorageReady(databaseModel))) {
        throw new PlannerViewPortError(
          'database_unavailable',
          'Database storage is unavailable'
        );
      }
      this.throwIfAborted(signal);
      return {
        dataSource: createDatabaseBlockDataSource(databaseModel),
        release,
      };
    } catch (error) {
      release();
      if (error instanceof PlannerViewPortError) throw error;
      throw new PlannerViewPortError(
        'database_unavailable',
        error instanceof Error ? error.message : String(error)
      );
    }
  }

  private describeViews(snapshot: readonly DataViewDataType[]) {
    const ids = new Set<string>();
    return snapshot.flatMap(view => {
      if (!supportedModes.has(view.mode as PlannerViewKind)) return [];
      if (ids.has(view.id) || !view.name.trim()) {
        throw new PlannerViewPortError(
          'create_failed',
          'Planner view metadata is invalid'
        );
      }
      ids.add(view.id);
      return [this.describeView(view)];
    });
  }

  private async waitForSourceView(
    dataSource: PlannerDatabaseDataSource,
    viewId: string,
    signal?: AbortSignal
  ): Promise<{
    snapshot: DataViewDataType[];
    sourceView: DataViewDataType;
  }> {
    this.throwIfAborted(signal);
    const read = () => {
      const snapshot = [...dataSource.viewDataList$.value];
      const sourceView = snapshot.find(view => view.id === viewId);
      return sourceView ? { snapshot, sourceView } : null;
    };
    const current = read();
    if (current) return current;

    return new Promise((resolve, reject) => {
      let settled = false;
      let unsubscribe: (() => void) | undefined;
      const cleanup = () => {
        clearTimeout(timeout);
        signal?.removeEventListener('abort', onAbort);
        unsubscribe?.();
      };
      const settle = (
        outcome:
          | { value: ReturnType<typeof read> }
          | { error: PlannerViewPortError }
      ) => {
        if (settled) return;
        settled = true;
        cleanup();
        if ('error' in outcome) reject(outcome.error);
        else if (outcome.value) resolve(outcome.value);
      };
      const onAbort = () =>
        settle({
          error: new PlannerViewPortError(
            'aborted',
            'Planner operation aborted'
          ),
        });
      const check = () => {
        const value = read();
        if (value) settle({ value });
      };
      const timeout = setTimeout(
        () =>
          settle({
            error: new PlannerViewPortError(
              'source_view_unavailable',
              'Planner source view is unavailable'
            ),
          }),
        SOURCE_VIEW_WAIT_TIMEOUT_MS
      );

      signal?.addEventListener('abort', onAbort, { once: true });
      if (signal?.aborted) {
        onAbort();
        return;
      }
      unsubscribe = dataSource.viewDataList$.subscribe(check);
      if (settled) unsubscribe();
      else check();
    });
  }

  private describeView(view: DataViewDataType): PlannerViewDescriptor {
    return {
      id: view.id,
      name: view.name.trim(),
      kind: view.mode as PlannerViewKind,
    };
  }

  private ganttCompatibility(
    dataSource: ReturnType<typeof createDatabaseBlockDataSource>,
    source: DataViewDataType
  ): PlannerViewCompatibility {
    const start = optionalString(source, 'startDateCol');
    const end = optionalString(source, 'endDateCol');
    if (!start || !dataSource.propertyTypeGet(start)) {
      return { compatible: false, reason: 'missing_start_date' };
    }
    if (
      !calendarRoleAcceptsPropertyType(
        'start',
        dataSource.propertyTypeGet(start)
      )
    ) {
      return { compatible: false, reason: 'invalid_start_date' };
    }
    if (!end || !dataSource.propertyTypeGet(end)) {
      return { compatible: false, reason: 'missing_end_date' };
    }
    if (
      start === end ||
      !calendarRoleAcceptsPropertyType('end', dataSource.propertyTypeGet(end))
    ) {
      return { compatible: false, reason: 'invalid_end_date' };
    }
    return { compatible: true, reason: null };
  }

  private kanbanCompatibility(
    dataSource: ReturnType<typeof createDatabaseBlockDataSource>,
    source: DataViewDataType
  ): PlannerViewCompatibility {
    const groupBy = optionalGroupBy(source);
    if (groupBy && isKanbanL1Groupable(dataSource, groupBy.columnId)) {
      return { compatible: true, reason: null };
    }
    const manager = new ViewManagerBase(dataSource);
    const calendarView = manager.viewGet(source.id) as
      | {
          // eslint-disable-next-line rxjs/finnish -- CalendarSingleView public signal contract
          statusCol$: { value: string | undefined };
        }
      | undefined;
    const status = calendarView?.statusCol$.value;
    return status &&
      dataSource.propertyTypeGet(status) &&
      isKanbanL1Groupable(dataSource, status) &&
      createKanbanL1GroupBy(dataSource, status)
      ? { compatible: true, reason: null }
      : { compatible: false, reason: 'missing_group_property' };
  }

  private withKanbanGroupBy(
    dataSource: ReturnType<typeof createDatabaseBlockDataSource>,
    source: DataViewDataType,
    data: DataViewDataType
  ): DataViewDataType {
    const current = optionalGroupBy(source);
    let groupBy: GroupBy | undefined =
      current && isKanbanL1Groupable(dataSource, current.columnId)
        ? current
        : undefined;
    if (!groupBy) {
      const manager = new ViewManagerBase(dataSource);
      const calendarView = manager.viewGet(source.id) as
        | {
            // eslint-disable-next-line rxjs/finnish -- CalendarSingleView public signal contract
            statusCol$: { value: string | undefined };
          }
        | undefined;
      const status = calendarView?.statusCol$.value;
      groupBy = status ? createKanbanL1GroupBy(dataSource, status) : undefined;
    }
    return { ...data, groupBy } as DataViewDataType;
  }

  private isAbortError(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'AbortError';
  }

  private throwIfAborted(signal?: AbortSignal): void {
    if (signal?.aborted) this.throwAborted();
  }

  private throwAborted(): never {
    throw new PlannerViewPortError('aborted', 'Planner operation aborted');
  }
}
